import { CSSProperties, ReactNode } from "react";

/**
 * Shared section wrapper for the Result / Price / List pages.
 * Establishes one consistent rhythm: 20px gutter, optional eyebrow + title,
 * optional right-side meta. Avoids each page reinventing its own headers.
 */
export function Section({
  eyebrow,
  title,
  meta,
  children,
  pad = 22,
  style,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  meta?: ReactNode;
  children?: ReactNode;
  /** Top padding (px). First section uses 14, others 22. */
  pad?: number;
  style?: CSSProperties;
}) {
  return (
    <section style={{ padding: `${pad}px 20px 0`, ...style }}>
      {(eyebrow || title || meta) && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 10,
          gap: 12,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {eyebrow && (
              <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 2 }}>
                {eyebrow}
              </div>
            )}
            {title && (
              <div className="analog-print" style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 800,
                fontSize: 22,
                color: "hsl(var(--ink))",
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
              }}>
                {title}
              </div>
            )}
          </div>
          {meta && (
            <div style={{ flexShrink: 0 }}>
              {meta}
            </div>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/** Standard card surface used inside sections. */
export function Card({
  children,
  style,
  accent,
}: {
  children: ReactNode;
  style?: CSSProperties;
  /** Optional left-border accent color, e.g. "hsl(var(--rust))". */
  accent?: string;
}) {
  return (
    <div className="analog-wobble" style={{
      background: "hsl(var(--cream))",
      border: "1px solid hsl(var(--ink))",
      borderLeft: accent ? `4px solid ${accent}` : undefined,
      borderRadius: "var(--radius-sm)",
      boxShadow: "var(--elevation-soft)",
      ...style,
    }}>
      {children}
    </div>
  );
}
